import { useState } from 'react';
import { Trash2, AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface DeleteProducerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  producerId: string;
  producerName: string;
  onSuccess?: () => void;
}

export function DeleteProducerDialog({
  open,
  onOpenChange,
  producerId,
  producerName,
  onSuccess,
}: DeleteProducerDialogProps) {
  const [confirmName, setConfirmName] = useState('');
  const [understood, setUnderstood] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const nameMatches = confirmName.trim().toLowerCase() === producerName.trim().toLowerCase();
  const canDelete = nameMatches && understood;

  const handleDelete = async () => {
    if (!canDelete) return;

    setIsSubmitting(true);
    try {
      const { data, error } = await supabase.rpc('delete_producer_cascade', {
        p_producer_id: producerId,
      });

      if (error) throw error;

      toast({
        title: 'Gården är raderad',
        description: `${producerName} och all tillhörande data har tagits bort.`,
      });

      onOpenChange(false);
      onSuccess?.();
    } catch (err: any) {
      console.error('Delete producer error:', err);
      toast({
        title: 'Kunde inte radera gården',
        description: err.message || 'Försök igen eller kontakta support.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Reset state when closing
      setConfirmName('');
      setUnderstood(false);
    }
    onOpenChange(newOpen);
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
              <Trash2 className="h-5 w-5 text-destructive" />
            </div>
            <AlertDialogTitle>Radera gård permanent?</AlertDialogTitle>
          </div>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              <p>
                Du är på väg att permanent radera <strong>{producerName}</strong>. 
                Detta kan inte ångras.
              </p>

              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
                <div className="flex gap-2">
                  <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                  <div className="text-sm space-y-1">
                    <p className="font-medium text-destructive">Följande data raderas:</p>
                    <ul className="list-disc list-inside text-muted-foreground space-y-0.5">
                      <li>Alla produkter och produktbilder</li>
                      <li>Alla event och bokningar</li>
                      <li>Alla ordrar och checkout-sessioner</li>
                      <li>Shopify-koppling</li>
                      <li>Teammedlemmar och inbjudningar</li>
                      <li>Alla uppladdade bilder</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="confirm-name">
              Skriv <span className="font-semibold">{producerName}</span> för att bekräfta
            </Label>
            <Input
              id="confirm-name"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder="Skriv gårdens namn..."
              autoComplete="off"
            />
          </div>

          <div className="flex items-start gap-2">
            <Checkbox
              id="understood"
              checked={understood}
              onCheckedChange={(checked) => setUnderstood(checked === true)}
            />
            <Label htmlFor="understood" className="text-sm leading-tight cursor-pointer">
              Jag förstår att detta inte kan ångras och att all data raderas permanent.
            </Label>
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isSubmitting}>Avbryt</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={!canDelete || isSubmitting}
          >
            {isSubmitting ? 'Raderar...' : 'Radera permanent'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
